import React, { useEffect, useState } from "react";
import { useLang } from "../../context/LangContext";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Lightbulb, AlertTriangle, Clock, MessageSquare } from "lucide-react";

const STATUSES = ["open", "resolved", "all"];
const KINDS = ["all", "suggestion", "complaint"];

export default function AdminFeedback() {
  const { t } = useLang();
  const [status, setStatus] = useState("open");
  const [kind, setKind] = useState("all");
  const [items, setItems] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/feedback", { params: { status, kind } });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, kind]);

  const resolve = async (id) => {
    try {
      await api.post(`/admin/feedback/${id}/resolve`);
      toast.success(t("mark_resolved"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const statusLabels = { open: t("feedback_status_open"), resolved: t("feedback_status_resolved"), all: t("filter_all") };
  const kindLabels = { all: t("filter_all"), suggestion: t("feedback_suggestion"), complaint: t("feedback_complaint") };

  return (
    <div className="space-y-4" data-testid="admin-feedback-page">
      <h1 className="text-2xl sm:text-3xl font-semibold flex items-center gap-2">
        <MessageSquare className="text-[#2A5948]" size={26} />
        {t("admin_feedback")}
      </h1>

      <div className="flex flex-wrap gap-3">
        <div className="flex gap-1 p-1 bg-white border border-[#E5E1D8] rounded-xl w-fit">
          {STATUSES.map((s) => (
            <button
              key={s}
              data-testid={`fb-filter-${s}`}
              onClick={() => setStatus(s)}
              className={`px-3 py-2 text-sm rounded-lg ${status === s ? "bg-[#2A5948] text-white" : "text-[#1C1E17] hover:bg-[#F3EFE7]"}`}
            >
              {statusLabels[s]}
            </button>
          ))}
        </div>
        <div className="flex gap-1 p-1 bg-white border border-[#E5E1D8] rounded-xl w-fit">
          {KINDS.map((k) => (
            <button
              key={k}
              data-testid={`fb-kind-${k}`}
              onClick={() => setKind(k)}
              className={`px-3 py-2 text-sm rounded-lg ${kind === k ? "bg-[#2A5948] text-white" : "text-[#1C1E17] hover:bg-[#F3EFE7]"}`}
            >
              {kindLabels[k]}
            </button>
          ))}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-[#E5E1D8] rounded-2xl p-8 text-center text-[#5C6053] text-sm">
          {t("no_feedback_yet")}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((f) => (
            <div key={f.id} data-testid={`fb-item-${f.id}`} className="bg-white border border-[#E5E1D8] rounded-2xl p-5 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${f.kind === "suggestion" ? "bg-[#E6F1E7] text-[#2A5948]" : "bg-[#FBE9E4] text-[#C96A52]"}`}>
                      {f.kind === "suggestion" ? <Lightbulb size={10} /> : <AlertTriangle size={10} />}
                      {t(`feedback_${f.kind}`)}
                    </span>
                    <h3 className="font-semibold">{f.subject}</h3>
                  </div>
                  <div className="text-xs text-[#5C6053] mt-1">
                    {f.user_name} · <span dir="ltr">{f.user_phone}</span> · {f.user_email}
                  </div>
                </div>
                <span className={`text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded flex-shrink-0 ${f.status === "resolved" ? "bg-[#E6F1E7] text-[#2A5948]" : "bg-[#FFF3D6] text-[#8A6D1F]"}`}>
                  {f.status === "resolved" ? <CheckCircle2 size={10} /> : <Clock size={10} />}
                  {t(`feedback_status_${f.status}`)}
                </span>
              </div>
              <p className="text-sm text-[#1C1E17] mt-3 whitespace-pre-wrap">{f.message}</p>
              <div className="mt-3 flex items-center justify-between">
                <div className="text-xs text-[#5C6053]">{f.created_at?.slice(0, 16).replace("T", " ")}</div>
                {f.status === "open" && (
                  <button
                    data-testid={`fb-resolve-${f.id}`}
                    onClick={() => resolve(f.id)}
                    className="px-3 py-1.5 text-xs rounded-lg brand-btn inline-flex items-center gap-1"
                  >
                    <CheckCircle2 size={14} /> {t("mark_resolved")}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
